package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.expr.ExprFormatter
import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.lifecycle.LifecycleUtils
import top.wcpe.allincore.ui.runtime.reactive.Effect
import kotlin.jvm.JvmField

/**
 * `$cond` 条件重组协作者：把 conditional 节点重组为透明 Box + 单活跃分支，依赖变化时
 * dispose 旧分支、实例化新分支。
 *
 * **依赖倒置**：构建活跃分支模板对「递归建树」的唯一回边经注入函数
 * `buildChild: (UiNode, Context) -> UIElement<*>` 表达；调用方（[ElementTreeBuilder]）
 * 把自己的 `::buildElement` 作实参传入。本协作者**不静态引用** [ElementTreeBuilder]。
 * 其余依赖（Context / ExprEvaluator / [ElementOverlayAttacher] / LifecycleUtils）照常直接调。
 */
internal object ConditionalRecomposer {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.IrInterpreter")

    /**
     * 构造 conditional 容器：包装一个透明 Box，根据 selectorExpr 求值结果选定唯一活跃分支。
     *
     * 核心机制：
     * 1. 注册一个 [Effect] 跟踪 selector 表达式依赖的 Signal；
     * 2. 每次依赖变化时同步重跑：求值 selector 得到分支索引；
     * 3. 分支索引未变化时直接 short-circuit；变化时递归 dispose 旧子树 + 实例化新分支模板，
     *    通过 [UIElement.replaceChildren] 替换容器子元素列表；
     * 4. selector 求值异常 / 索引越界时容器进入空状态，下次依赖变化仍可恢复。
     *
     * 分支模板就是 `node.getChildren()`：editor 编译期把 N 个 branch.body 与 elseBranch
     * 顺序写入 children 列表，selector 求值返回的索引直接对应 children[i]。selector 返回 -1
     * 或越界时容器 children 清空。
     */
    @Suppress("TooGenericExceptionCaught", "LongMethod", "NestedBlockDepth")
    fun buildConditionalContainer(
        node: UiNode,
        ctx: IrInterpreter.Context,
        buildChild: (UiNode, IrInterpreter.Context) -> UIElement<*>,
    ): UIElement<*> {
        // 容器：透明 Box（无视觉）。id 沿用 node 的 "$cond_l_c"。标记为透明布局容器
        // （display: contents）：flex 父下布局引擎摊平它、把分支项直接挂到父，使父的
        // gap/align/justify/wrap 真正作用于分支项（见 LayoutNode.transparentLayout）。
        val container: UIElement<*> = IrElementFactory.create("box")
        container.id(node.id)
        container.layoutTransparent(true)

        // conditional 容器保证 selectorExpr 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
        val selectorExprNode: ExprNode = ExprBlobReader.readExpr(node.selectorExpr!!)
        val templates: List<UiNode> = node.children

        // 首次失败时输出完整 stack trace 便于定位 selector 表达式 bug；
        // 后续重复失败只打消息，避免日志 spam。
        // 节流粒度按 node.getId()（含 DSL 行列号），跨 Effect 重建（如 hover overlay 每次重新
        // 实例化 conditional）共享，避免相同位置反复打堆栈。
        val conditionalId: String = node.id

        val effect = Effect {
            try {
                val selectedValue: Any? = ExprEvaluator.eval(selectorExprNode, ctx.evalCtx)
                val selectedIndex = coerceConditionalIndex(selectedValue)

                val oldState = ctx.conditionalState[container]
                if (oldState != null && oldState.index == selectedIndex) {
                    // 同分支命中：复用旧子元素，不重新实例化（短路重组开销）
                    return@Effect
                }

                // 旧分支递归 dispose（先从 map 清掉避免 catch 路径再次重复 dispose）
                if (oldState != null) {
                    ctx.conditionalState.remove(container)
                    if (oldState.child != null) {
                        LifecycleUtils.disposeRecursively(oldState.child)
                    }
                }

                // 新分支实例化（索引在 children 范围内）
                var newChild: UIElement<*>? = null
                if (selectedIndex >= 0 && selectedIndex < templates.size) {
                    val selected = templates[selectedIndex]
                    newChild = buildChild(selected, ctx.withBranchNode(selectedIndex, selected))
                    // 循环槽 tooltip 修复:conditional 分支动态实例化不经过初始 attachElementOverlays,
                    // 在此对分支子树补挂 overlay 描述符(嵌套在 for 内时 ctx.evalCtx 含 cell 形参,一并捕获)。
                    ElementOverlayAttacher.attachTemplateOverlays(newChild, ctx)
                }

                if (newChild == null) {
                    container.replaceChildren(emptyList<UIElement<*>>())
                } else {
                    container.replaceChildren(listOf(newChild))
                }
                // 一次写入：状态原子化，杜绝 child / index 不一致
                ctx.conditionalState[container] = ConditionalState(selectedIndex, newChild)
            } catch (t: Throwable) {
                // 节流：相同 node.getId() 跨多次 buildConditionalContainer 调用（如 hover overlay
                // 反复实例化）只打一次堆栈
                val firstFailureForThisNode = ctx.conditionalFailedIds.add(conditionalId)
                if (firstFailureForThisNode) {
                    // 首次失败：完整 stack trace + selector 表达式可读形式 + 容器位置
                    // node.getId() 形如 "$cond_<line>_<col>"（来自编译期 wrapConditional），
                    // selectorExprNode.toString() 是嵌套 Ternary/BinaryOp 树，能显示触发点表达式。
                    LOGGER.warn(
                        "[ui] conditional 容器 $conditionalId selector 重建失败（首次，详细堆栈）\n  位置：${describeConditionalLocation(
                            conditionalId,
                        )}\n  selector 表达式：${ExprFormatter.format(selectorExprNode)}",
                        t,
                    )
                } else {
                    // 后续失败：只打消息避免 log spam
                    LOGGER.warn("[ui] conditional 容器 $conditionalId selector 重建再次失败：$t")
                }
                // 容器进入空状态，下次依赖变化仍可恢复
                val stale = ctx.conditionalState.remove(container)
                if (stale != null && stale.child != null) {
                    LifecycleUtils.disposeRecursively(stale.child)
                }
                container.replaceChildren(emptyList<UIElement<*>>())
            }
        }

        container.lifecycleScope().register(effect)
        return container
    }

    /**
     * 把 selector 表达式求值结果统一转为 children 索引（int）。
     *
     * - [Number] → intValue()
     * - [Boolean] → true=0 / false=-1（理论上 selector 永远是 NumberLit，这里仅做兜底）
     * - null → -1（无命中）
     * - 其他类型 → -1（warn 日志一次）
     */
    @Suppress("ReturnCount")
    private fun coerceConditionalIndex(value: Any?): Int {
        if (value == null) return -1
        if (value is Number) return value.toInt()
        if (value is Boolean) return if (value) 0 else -1
        LOGGER.warn("[ui] conditional selector 返回非数字结果（type=${value::class.simpleName}），按未命中处理")
        return -1
    }

    /**
     * 把 conditional 节点 id 解析为可读的 DSL 源码位置。
     *
     * id 格式由编译期 `wrapConditional` 生成：`"$cond_<line>_<col>"`。
     * 例如 `"$cond_145_15"` 表示用户 DSL 文件的第 145 行第 15 列（1-based）——
     * 这是用户写 if/when 的关键字位置。
     *
     * 解析失败时返回 id 原文，避免日志层 NPE。
     */
    @Suppress("ReturnCount")
    private fun describeConditionalLocation(id: String?): String {
        if (id == null) return "unknown"
        // 仅做最直白的拆分，避免引入正则编译开销
        val firstUnderscore = id.indexOf('_')
        if (firstUnderscore < 0) return id
        val secondUnderscore = id.indexOf('_', firstUnderscore + 1)
        if (secondUnderscore < 0) return id
        val linePart = id.substring(firstUnderscore + 1, secondUnderscore)
        val colPart = id.substring(secondUnderscore + 1)
        return "DSL 源码 第 $linePart 行 第 $colPart 列"
    }

    /**
     * RFC-RECOMPOSITION：conditional 容器的当前命中状态。
     *
     * 把活跃 child 引用与命中索引合并为单一对象，避免两表不同步。Effect 重跑时
     * 一次性读 / 写 ConditionalState，要么完整旧状态要么完整新状态，不会出现"index
     * 已更新但 child 没更新"的中间不一致。
     */
    internal class ConditionalState(
        /** 当前命中分支 index；-1 表示未命中（child 必为 null）。 */
        @JvmField val index: Int,
        /** 当前活跃子元素；未命中时为 null。 */
        @JvmField val child: UIElement<*>?,
    )
}
